import React, { useState } from 'react';
import {
  X,
  Settings,
  Film,
  FolderPlus,
  Calendar,
  MessageSquare,
  Users,
  Check,
  Trash2,
  Edit2,
  Plus,
  ExternalLink,
  ShieldCheck,
  Video,
  Share2,
  Globe,
  Instagram,
  Youtube,
  Linkedin,
  Zap,
  QrCode,
  Copy,
  ArrowUp,
  ArrowDown,
  Loader2,
  Book,
  Award,
  Image as ImageIcon,
} from 'lucide-react';
import {
  MentorAreaConfig,
  MentorModule,
  MentorLesson,
  MentorEvent,
  MentorStudent,
  BioLinkItem,
  MentorBioConfig,
  MentorSocialLinks,
  SocialEntry,
  normalizeSocials,
} from '../../types/mentorship';
import { BioQRCodeModal } from './BioQRCodeModal';
import {
  WhatsAppIcon,
  InstagramIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
  XTwitterIcon,
  GitHubIcon,
} from './SocialIcons';
import ImageUploader from '../ImageUploader';

interface MentorAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: MentorAreaConfig;
  onSaveConfig: (newConfig: MentorAreaConfig) => void;
  initialTab?: 'geral' | 'hero' | 'modulos' | 'eventos' | 'suporte' | 'alunos' | 'bio';
}

export const MentorAdminModal: React.FC<MentorAdminModalProps> = ({
  isOpen,
  onClose,
  config,
  onSaveConfig,
  initialTab = 'geral',
}) => {
  const [activeTab, setActiveTab] = useState<'geral' | 'hero' | 'modulos' | 'eventos' | 'suporte' | 'alunos' | 'bio'>(initialTab);
  const [formData, setFormData] = useState<MentorAreaConfig>({ ...config });

  const prevIsOpenRef = React.useRef(false);
  const prevMentorKeyRef = React.useRef<string>('');

  React.useEffect(() => {
    const currentKey = config ? `${config.id || ''}-${config.slug || ''}` : '';
    const wasOpened = isOpen && !prevIsOpenRef.current;
    const mentorChanged = currentKey !== prevMentorKeyRef.current;

    // Só sincroniza o formData quando o modal está abrindo ou quando troca de mentor.
    // Isso impede que re-renders ou saves em background sobreponham o que o usuário está digitando.
    if (isOpen && (wasOpened || mentorChanged)) {
      if (config) {
        setFormData({ ...config });
      }
      if (initialTab) {
        setActiveTab(initialTab);
      }
      prevMentorKeyRef.current = currentKey;
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, config, initialTab]);

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [bioSaveSuccess, setBioSaveSuccess] = useState(false);

  // States para Módulos / Aulas / Eventos / Alunos
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDesc, setNewModuleDesc] = useState('');
  const [newModuleCover, setNewModuleCover] = useState('');

  const [selectedModuleIdForLesson, setSelectedModuleIdForLesson] = useState<string>('');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonYoutube, setNewLessonYoutube] = useState('');
  const [newLessonDuration, setNewLessonDuration] = useState('10 min');
  const [newLessonDesc, setNewLessonDesc] = useState('');

  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTime, setNewEventTime] = useState('20:00');
  const [newEventDesc, setNewEventDesc] = useState('');
  const [newEventUrl, setNewEventUrl] = useState('');

  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [newStudentName, setNewStudentName] = useState('');

  // States para Bio Links (Linktree)
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [newBioTitle, setNewBioTitle] = useState('');
  const [newBioSubtitle, setNewBioSubtitle] = useState('');
  const [newBioUrl, setNewBioUrl] = useState('');
  const [newBioIcon, setNewBioIcon] = useState('Zap');
  const [newBioCustomIconUrl, setNewBioCustomIconUrl] = useState('');
  const [newBioFeatured, setNewBioFeatured] = useState(false);
  const [newBioBadge, setNewBioBadge] = useState('🔥 RECOMENDADO');

  if (!isOpen) return null;

  const accentColor = formData.branding.accentColor || '#0FFF55';
  const rawBioConfig = formData.bioConfig as any;
  const currentBio: MentorBioConfig = {
    isEnabled: true,
    slug: formData.slug,
    bioTitle: formData.mentorName,
    bioSubtitle: formData.mentorTitle,
    bioAvatarUrl: formData.mentorAvatarUrl,
    socials: normalizeSocials(rawBioConfig?.socials),
    links: [],
    ...(rawBioConfig ? { ...rawBioConfig, socials: normalizeSocials(rawBioConfig.socials) } : {}),
  };

  // Helper: retorna entradas de uma rede social como SocialEntry[]
  const getSocialEntries = (key: keyof MentorSocialLinks): SocialEntry[] =>
    (currentBio.socials as any)?.[key] || [];

  // Helper: atualiza entradas de uma rede social no formData sem descartar durante a digitação
  const updateSocialEntries = (key: keyof MentorSocialLinks, entries: SocialEntry[]) => {
    setFormData((prev) => {
      const bioConf = prev.bioConfig || currentBio;
      const prevSocials = (bioConf as any).socials || {};
      return {
        ...prev,
        bioConfig: {
          ...bioConf,
          socials: {
            ...prevSocials,
            [key]: entries,
          },
        },
      };
    });
  };

  const cleanConfigForSave = (data: MentorAreaConfig): MentorAreaConfig => {
    const socials = data.bioConfig?.socials;
    if (!socials) return data;
    const cleanedSocials: Record<string, SocialEntry[]> = {};
    for (const [k, arr] of Object.entries(socials)) {
      if (Array.isArray(arr)) {
        const valid = arr
          .map((e: any) => ({
            url: typeof e.url === 'string' ? e.url.trim() : '',
            label: typeof e.label === 'string' ? e.label.trim() : undefined,
          }))
          .filter((e) => Boolean(e.url));
        if (valid.length > 0) cleanedSocials[k] = valid;
      }
    }
    return {
      ...data,
      bioConfig: {
        ...(data.bioConfig as any),
        socials: cleanedSocials,
      },
    };
  };

  const bioPageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/bio-${formData.slug}`
    : `https://masterclubbr.vip/bio-${formData.slug}`;

  const handleCopyBioUrl = () => {
    navigator.clipboard.writeText(bioPageUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleSaveGeneral = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveConfig(cleanConfigForSave(formData));
    onClose();
  };

  const handleSaveBioHeader = async () => {
    setIsSavingBio(true);
    try {
      await onSaveConfig(cleanConfigForSave(formData));
      setBioSaveSuccess(true);
      setTimeout(() => setBioSaveSuccess(false), 2500);
    } catch (err) {
      console.error('Erro ao salvar dados da bio:', err);
    } finally {
      setIsSavingBio(false);
    }
  };

  // Handlers Módulos e Aulas
  const handleAddModule = () => {
    if (!newModuleTitle.trim()) return;
    const newMod: MentorModule = {
      id: `mod-${Date.now()}`,
      order: formData.modules.length + 1,
      title: newModuleTitle.toUpperCase(),
      description: newModuleDesc,
      coverImageUrl: newModuleCover || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=600',
      accentGlowColor: accentColor,
      lessons: [],
    };
    const updated = { ...formData, modules: [...formData.modules, newMod] };
    setFormData(updated);
    onSaveConfig(updated);
    setNewModuleTitle('');
    setNewModuleDesc('');
    setNewModuleCover('');
  };

  const handleDeleteModule = (modId: string) => {
    if (!confirm('Deseja realmente excluir este módulo e suas aulas?')) return;
    const updated = { ...formData, modules: formData.modules.filter((m) => m.id !== modId) };
    setFormData(updated);
    onSaveConfig(updated);
  };

  const handleAddLesson = (modId: string) => {
    if (!newLessonTitle.trim() || !newLessonYoutube.trim()) return;
    const newLes: MentorLesson = {
      id: `les-${Date.now()}`,
      order: 1,
      title: newLessonTitle,
      videoUrl: newLessonYoutube,
      durationText: newLessonDuration || '10 min',
      thumbnailUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=600',
      publishedAtText: `Publicado em ${new Date().toLocaleDateString('pt-BR')}`,
      description: newLessonDesc,
      isCompleted: false,
    };

    const updatedModules = formData.modules.map((m) => {
      if (m.id === modId) {
        return { ...m, lessons: [...m.lessons, newLes] };
      }
      return m;
    });

    const updated = { ...formData, modules: updatedModules };
    setFormData(updated);
    onSaveConfig(updated);
    setNewLessonTitle('');
    setNewLessonYoutube('');
    setNewLessonDesc('');
    setSelectedModuleIdForLesson('');
  };

  const handleDeleteLesson = (modId: string, lesId: string) => {
    const updatedModules = formData.modules.map((m) => {
      if (m.id === modId) {
        return { ...m, lessons: m.lessons.filter((l) => l.id !== lesId) };
      }
      return m;
    });
    const updated = { ...formData, modules: updatedModules };
    setFormData(updated);
    onSaveConfig(updated);
  };

  // Handlers Eventos
  const handleAddEvent = () => {
    if (!newEventTitle.trim() || !newEventDate.trim()) return;
    const newEv: MentorEvent = {
      id: `ev-${Date.now()}`,
      title: newEventTitle,
      dateText: newEventDate,
      timeText: newEventTime,
      description: newEventDesc,
      meetingUrl: newEventUrl || 'https://zoom.us',
      isLive: true,
    };
    const currentEvents = formData.events || [];
    const updated = { ...formData, events: [...currentEvents, newEv] };
    setFormData(updated);
    onSaveConfig(updated);
    setNewEventTitle('');
    setNewEventDate('');
    setNewEventDesc('');
    setNewEventUrl('');
  };

  const handleDeleteEvent = (evId: string) => {
    const currentEvents = formData.events || [];
    const updated = { ...formData, events: currentEvents.filter((e) => e.id !== evId) };
    setFormData(updated);
    onSaveConfig(updated);
  };

  // Handlers Alunos
  const handleAddStudent = () => {
    if (!newStudentEmail.trim()) return;
    const newSt: MentorStudent = {
      id: `st-${Date.now()}`,
      name: newStudentName || newStudentEmail.split('@')[0],
      email: newStudentEmail,
      accessDateText: new Date().toLocaleDateString('pt-BR'),
      progressPercentage: 0,
    };
    const currentStudents = formData.students || [];
    const updated = { ...formData, students: [...currentStudents, newSt] };
    setFormData(updated);
    onSaveConfig(updated);
    setNewStudentEmail('');
    setNewStudentName('');
  };

  // Handlers Bio Links (Linktree) - Adicionar / Editar / Reordenar / Deletar
  const handleStartEditBioLink = (link: BioLinkItem) => {
    setEditingLinkId(link.id);
    setNewBioTitle(link.title);
    setNewBioSubtitle(link.subtitle || '');
    setNewBioUrl(link.url);
    const customImg =
      link.thumbnailUrl ||
      link.iconUrl ||
      (link.icon &&
      (link.icon.startsWith('http') || link.icon.startsWith('/') || link.icon.startsWith('data:'))
        ? link.icon
        : '');

    if (customImg) {
      setNewBioIcon('Custom');
      setNewBioCustomIconUrl(customImg);
    } else {
      setNewBioIcon(link.icon || 'Zap');
      setNewBioCustomIconUrl('');
    }
    setNewBioFeatured(!!link.isFeatured);
    setNewBioBadge(link.badgeText || '');
  };

  const handleCancelEditBioLink = () => {
    setEditingLinkId(null);
    setNewBioTitle('');
    setNewBioSubtitle('');
    setNewBioUrl('');
    setNewBioIcon('Zap');
    setNewBioCustomIconUrl('');
    setNewBioFeatured(false);
    setNewBioBadge('');
  };

  const handleAddOrUpdateBioLink = () => {
    if (!newBioTitle.trim() || !newBioUrl.trim()) return;

    const trimmedCustom = newBioCustomIconUrl.trim();
    const finalIcon = newBioIcon === 'Custom' ? (trimmedCustom || 'Globe') : newBioIcon;
    const finalThumbnail = trimmedCustom || undefined;

    let updatedLinks: BioLinkItem[];

    if (editingLinkId) {
      updatedLinks = currentBio.links.map((link) =>
        link.id === editingLinkId
          ? {
              ...link,
              title: newBioTitle.trim(),
              subtitle: newBioSubtitle.trim() || undefined,
              url: newBioUrl.trim(),
              icon: finalIcon,
              thumbnailUrl: finalThumbnail,
              iconUrl: finalThumbnail,
              isFeatured: newBioFeatured,
              badgeText: newBioFeatured ? (newBioBadge.trim() || undefined) : undefined,
            }
          : link
      );
    } else {
      const newLink: BioLinkItem = {
        id: `link-${Date.now()}`,
        title: newBioTitle.trim(),
        subtitle: newBioSubtitle.trim() || undefined,
        url: newBioUrl.trim(),
        icon: finalIcon,
        thumbnailUrl: finalThumbnail,
        iconUrl: finalThumbnail,
        isFeatured: newBioFeatured,
        badgeText: newBioFeatured ? (newBioBadge.trim() || undefined) : undefined,
      };
      updatedLinks = [...currentBio.links, newLink];
    }

    const updatedBio: MentorBioConfig = {
      ...currentBio,
      links: updatedLinks,
    };
    const updated = { ...formData, bioConfig: updatedBio };
    setFormData(updated);
    onSaveConfig(updated);

    handleCancelEditBioLink();
  };

  const handleMoveBioLink = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentBio.links.length) return;

    const linksCopy = [...currentBio.links];
    const temp = linksCopy[index];
    linksCopy[index] = linksCopy[targetIndex];
    linksCopy[targetIndex] = temp;

    const updatedBio: MentorBioConfig = {
      ...currentBio,
      links: linksCopy,
    };
    const updated = { ...formData, bioConfig: updatedBio };
    setFormData(updated);
    onSaveConfig(updated);
  };

  const handleDeleteBioLink = (linkId: string) => {
    const updatedBio: MentorBioConfig = {
      ...currentBio,
      links: currentBio.links.filter((l) => l.id !== linkId),
    };
    const updated = { ...formData, bioConfig: updatedBio };
    setFormData(updated);
    onSaveConfig(updated);
    if (editingLinkId === linkId) {
      handleCancelEditBioLink();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in select-none">
      <div className="w-full max-w-6xl max-h-[90vh] flex flex-col rounded-3xl bg-[#17181c] border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#121316]">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5" style={{ color: accentColor }} />
            <div>
              <h2 className="text-lg font-bold text-white leading-none">Painel de Gestão da Mentoria</h2>
              <p className="text-xs text-gray-400 mt-1">Configure elementos, cursos, eventos, suporte e bio links</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation (Largura ampla sem barra de rolagem) */}
        <div className="flex items-center justify-between gap-1.5 px-6 py-3 border-b border-white/10 bg-[#141519] flex-wrap sm:flex-nowrap">
          {[
            { id: 'geral', label: 'Branding & URL', icon: Settings },
            { id: 'bio', label: 'Links da Bio (Linktree)', icon: Share2 },
            { id: 'hero', label: 'Vídeo Hero', icon: Film },
            { id: 'modulos', label: 'Módulos & Aulas', icon: FolderPlus },
            { id: 'eventos', label: 'Eventos', icon: Calendar },
            { id: 'suporte', label: 'Suporte WhatsApp', icon: MessageSquare },
            { id: 'alunos', label: 'Alunos (Dashboard)', icon: Users },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-white/10 text-white border border-white/15 shadow-sm'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                style={{ borderColor: isActive ? accentColor : undefined }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: isActive ? accentColor : undefined }} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6 overflow-y-auto space-y-6">
          {/* TAB: LINKS DA BIO (LINKTREE) */}
          {activeTab === 'bio' && (
            <div className="space-y-6">
              {/* Header Box com Link Público e QR Code */}
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold text-[#0FFF55] uppercase">
                    <Share2 className="w-4 h-4" />
                    <span>Seu Linktree Exclusivo MasterClub</span>
                  </div>
                  <div className="text-xs text-gray-300 font-mono mt-1 select-all">{bioPageUrl}</div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleCopyBioUrl}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs transition-all"
                  >
                    {copiedLink ? <Check className="w-3.5 h-3.5 text-[#0FFF55]" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedLink ? 'Copiado!' : 'Copiar URL'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsQrModalOpen(true)}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs transition-all shadow"
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    <span>QR Code</span>
                  </button>

                  <a
                    href={`/bio-${formData.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-xl bg-white/10 hover:bg-white/15 text-white"
                    title="Visualizar Página Bio"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Informações da Bio & Redes Sociais */}
              {/* Foto da Bio / Avatar com Drag & Drop */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                  <ImageUploader
                    bucketName="mentorias"
                    label="Foto do Perfil da Bio (Drag & Drop)"
                    value={currentBio.bioAvatarUrl || formData.mentorAvatarUrl}
                    onChange={(url) => {
                      setFormData((prev) => ({
                        ...prev,
                        mentorAvatarUrl: url,
                        bioConfig: {
                          ...(prev.bioConfig || currentBio),
                          bioAvatarUrl: url,
                        },
                      }));
                    }}
                    options={{ maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' }}
                    placeholder="Arraste a foto ou selecione o arquivo..."
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    * Proporção ideal: <strong className="text-[#0FFF55]">400x400 (1:1)</strong>. Atualiza o card e a bio.
                  </span>
                </div>

                <div className="sm:col-span-2 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-300 mb-1">Título da Bio (Nome)</label>
                    <input
                      type="text"
                      value={currentBio.bioTitle}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          mentorName: val,
                          bioConfig: {
                            ...(prev.bioConfig || currentBio),
                            bioTitle: val,
                          },
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-300 mb-1">Bio / Tagline Curto</label>
                    <input
                      type="text"
                      value={currentBio.bioSubtitle || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          mentorTitle: val,
                          bioConfig: {
                            ...(prev.bioConfig || currentBio),
                            bioSubtitle: val,
                          },
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Redes Sociais — Multi-Link */}
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase text-gray-300">Links das Suas Redes Sociais</h3>
                  <span className="text-[10px] text-gray-500 bg-white/5 px-2 py-1 rounded-lg">
                    ✦ Suporte a múltiplos perfis por rede
                  </span>
                </div>

                {/* Componente SocialMultiInput — renderizado inline para cada rede */}
                {([
                  { key: 'instagram' as const, label: 'Instagram', icon: <InstagramIcon className="w-3.5 h-3.5 text-[#E4405F]" />, placeholder: 'https://instagram.com/seuuser', color: '#E4405F' },
                  { key: 'youtube' as const, label: 'YouTube', icon: <YouTubeIcon className="w-3.5 h-3.5 text-[#FF0000]" />, placeholder: 'https://youtube.com/@seucanal', color: '#FF0000' },
                  { key: 'whatsapp' as const, label: 'WhatsApp', icon: <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />, placeholder: 'https://wa.me/5511... ou https://chat.whatsapp.com/...', color: '#25D366' },
                  { key: 'linkedin' as const, label: 'LinkedIn', icon: <LinkedInIcon className="w-3.5 h-3.5 text-[#0A66C2]" />, placeholder: 'https://linkedin.com/in/seuuser', color: '#0A66C2' },
                  { key: 'tiktok' as const, label: 'TikTok', icon: <TikTokIcon className="w-3.5 h-3.5 text-white" />, placeholder: 'https://tiktok.com/@seuuser', color: '#ffffff' },
                  { key: 'twitter' as const, label: 'X (Twitter)', icon: <XTwitterIcon className="w-3.5 h-3.5 text-white" />, placeholder: 'https://x.com/seuuser', color: '#ffffff' },
                  { key: 'github' as const, label: 'GitHub', icon: <GitHubIcon className="w-3.5 h-3.5 text-white" />, placeholder: 'https://github.com/seuuser', color: '#ffffff' },
                  { key: 'website' as const, label: 'Website / Site Oficial', icon: <Globe className="w-3.5 h-3.5 text-[#0FFF55]" />, placeholder: 'https://seusite.com', color: '#0FFF55' },
                ] as const).map(({ key, label, icon, placeholder, color }) => {
                  const entries = getSocialEntries(key);
                  return (
                    <div key={key} className="border border-white/8 rounded-xl p-3 space-y-2 bg-[#0d0e11]/60">
                      {/* Header da rede */}
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-1.5 text-[11px] font-bold text-gray-300">
                          {icon} {label}
                          {entries.length > 1 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-white/10 text-gray-400">
                              {entries.length} perfis
                            </span>
                          )}
                        </label>
                        <button
                          type="button"
                          onClick={() => updateSocialEntries(key, [...entries, { url: '', label: '' }])}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-all hover:bg-white/10"
                          style={{ color }}
                        >
                          <Plus className="w-3 h-3" />
                          Adicionar {label}
                        </button>
                      </div>

                      {/* Lista de entradas */}
                      {entries.length === 0 ? (
                        <p className="text-[10px] text-gray-600 italic pl-1">Nenhum link cadastrado. Clique em "Adicionar" para incluir.</p>
                      ) : (
                        <div className="space-y-2">
                          {entries.map((entry, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <div className="flex-1 space-y-1.5">
                                <input
                                  type="url"
                                  placeholder={placeholder}
                                  value={entry.url}
                                  onChange={(e) => {
                                    const next = entries.map((en, i) => i === idx ? { ...en, url: e.target.value } : en);
                                    updateSocialEntries(key, next);
                                  }}
                                  className="w-full px-3 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white placeholder-gray-600 focus:border-white/30 focus:outline-none"
                                />
                                <input
                                  type="text"
                                  placeholder={`Label opcional: ex. ${label} do Sócio`}
                                  value={entry.label || ''}
                                  onChange={(e) => {
                                    const next = entries.map((en, i) => i === idx ? { ...en, label: e.target.value } : en);
                                    updateSocialEntries(key, next);
                                  }}
                                  className="w-full px-3 py-1.5 bg-[#17181c] border border-white/10 rounded-xl text-[11px] text-gray-400 placeholder-gray-700 focus:border-white/25 focus:outline-none"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const next = entries.filter((_, i) => i !== idx);
                                  updateSocialEntries(key, next);
                                }}
                                className="mt-1.5 p-2 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-400/10 transition-all flex-shrink-0"
                                title="Remover este link"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Botão de Salvar Alterações da Bio e Redes Sociais */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-[#121316] border border-white/10 rounded-2xl gap-3">
                <div>
                  <div className="text-xs font-bold text-white">Salvar Dados da Bio & Redes Sociais</div>
                  <div className="text-[11px] text-gray-400">
                    A foto, o nome, a tagline e os links das redes serão salvos sem recarregar a tela.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSaveBioHeader}
                  disabled={isSavingBio}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#0FFF55]/20 transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSavingBio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  <span>{bioSaveSuccess ? 'Salvo com Sucesso!' : isSavingBio ? 'Salvando...' : 'Salvar Dados da Bio'}</span>
                </button>
              </div>

              {/* Form Adicionar / Editar Link no Linktree */}
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase text-[#0FFF55]">
                    {editingLinkId ? 'Editar Botão de Link' : 'Adicionar Novo Link de Acesso'}
                  </h3>
                  {editingLinkId && (
                    <button
                      type="button"
                      onClick={handleCancelEditBioLink}
                      className="text-xs text-gray-400 hover:text-white underline"
                    >
                      Cancelar Edição
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Título do Botão (ex: ⚡ Mentoria VIP)"
                    value={newBioTitle}
                    onChange={(e) => setNewBioTitle(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                  <input
                    type="text"
                    placeholder="Subtítulo / Descrição breve"
                    value={newBioSubtitle}
                    onChange={(e) => setNewBioSubtitle(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="url"
                    placeholder="URL de Destino (ex: https://... ou /mentoria-slug)"
                    value={newBioUrl}
                    onChange={(e) => setNewBioUrl(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white sm:col-span-2"
                  />

                  <select
                    value={newBioIcon}
                    onChange={(e) => setNewBioIcon(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  >
                    <option value="Zap">⚡ Raio (Destaque)</option>
                    <option value="MessageSquare">💬 WhatsApp / Chat</option>
                    <option value="Globe">🌐 Website / Web</option>
                    <option value="Instagram">📸 Instagram</option>
                    <option value="Video">🎥 Vídeo / YouTube</option>
                    <option value="TikTok">🎵 TikTok</option>
                    <option value="LinkedIn">💼 LinkedIn</option>
                    <option value="XTwitter">🐦 X / Twitter</option>
                    <option value="GitHub">🐙 GitHub</option>
                    <option value="Book">📚 Livro / E-book</option>
                    <option value="Award">🏆 Troféu / Conquista</option>
                    <option value="Custom">🖼️ Ícone / Imagem Personalizada</option>
                  </select>
                </div>

                {/* Seção de Ícone Personalizado via URL ou Upload de Imagem */}
                {(newBioIcon === 'Custom' || newBioCustomIconUrl) && (
                  <div className="p-3.5 rounded-2xl bg-[#121316] border border-[#0FFF55]/30 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-[#0FFF55]" />
                        <span className="text-xs font-bold text-white">Ícone / Imagem Personalizada</span>
                      </div>
                      <span className="text-[10px] text-[#0FFF55] font-semibold bg-[#0FFF55]/10 px-2.5 py-0.5 rounded-full border border-[#0FFF55]/20 self-start sm:self-auto">
                        💡 Recomendado: 64x64px a 128x128px (PNG transparente ou WebP)
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3.5">
                      {/* Visual Live Preview */}
                      <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-white/5 border-2 border-[#0FFF55]/50 flex items-center justify-center overflow-hidden shadow-lg shadow-[#0FFF55]/10">
                          {newBioCustomIconUrl ? (
                            <img
                              src={newBioCustomIconUrl}
                              alt="Pré-visualização do Ícone"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <span className="text-[9px] text-gray-400 font-medium">Preview</span>
                      </div>

                      {/* Inputs: URL direta e Upload */}
                      <div className="flex-1 w-full space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            placeholder="Cole a URL do ícone (ex: https://meusite.com/logo.png)"
                            value={newBioCustomIconUrl}
                            onChange={(e) => {
                              setNewBioCustomIconUrl(e.target.value);
                              if (e.target.value) setNewBioIcon('Custom');
                            }}
                            className="flex-1 px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white font-mono"
                          />
                          {newBioCustomIconUrl && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewBioCustomIconUrl('');
                                if (newBioIcon === 'Custom') setNewBioIcon('Zap');
                              }}
                              className="px-2.5 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-semibold transition-all cursor-pointer"
                              title="Remover imagem"
                            >
                              Limpar
                            </button>
                          )}
                        </div>

                        <ImageUploader
                          bucketName="mentorias"
                          label="Ou Envie o Arquivo da Imagem / Logo"
                          value={newBioCustomIconUrl}
                          onChange={(url) => {
                            setNewBioCustomIconUrl(url);
                            setNewBioIcon('Custom');
                          }}
                          options={{ maxWidth: 256, maxHeight: 256, quality: 0.9, format: 'webp' }}
                          placeholder="Clique para fazer upload de PNG/WebP/SVG..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBioFeatured}
                      onChange={(e) => setNewBioFeatured(e.target.checked)}
                      className="rounded border-white/20 text-[#0FFF55]"
                    />
                    <span>Destacar Link com Brilho Neon e Pulso</span>
                  </label>

                  {newBioFeatured && (
                    <input
                      type="text"
                      placeholder="Selo (ex: 🔥 MAIS POPULAR)"
                      value={newBioBadge}
                      onChange={(e) => setNewBioBadge(e.target.value)}
                      className="px-3 py-1 bg-[#17181c] border border-white/15 rounded-lg text-xs text-white"
                    />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAddOrUpdateBioLink}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs rounded-xl shadow transition-all active:scale-95 cursor-pointer"
                  >
                    {editingLinkId ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    <span>{editingLinkId ? 'Atualizar Botão de Link' : 'Adicionar Botão de Link'}</span>
                  </button>

                  {editingLinkId && (
                    <button
                      type="button"
                      onClick={handleCancelEditBioLink}
                      className="px-3 py-2 bg-white/10 hover:bg-white/15 text-xs text-gray-300 font-bold rounded-xl transition-all cursor-pointer"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de Links Cadastrados com Edição e Reordenação */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase">Botões Cadastrados ({currentBio.links.length})</h4>
                {currentBio.links.map((link, idx) => {
                  const customImg =
                    link.thumbnailUrl ||
                    link.iconUrl ||
                    (link.icon &&
                    (link.icon.startsWith('http') || link.icon.startsWith('/') || link.icon.startsWith('data:'))
                      ? link.icon
                      : null);

                  const renderItemIcon = () => {
                    if (customImg) {
                      return (
                        <img
                          src={customImg}
                          alt={link.title}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      );
                    }
                    switch (link.icon) {
                      case 'Zap':
                      case 'Lightning':
                        return <Zap className="w-4 h-4 text-[#0FFF55]" />;
                      case 'MessageSquare':
                      case 'WhatsApp':
                      case 'whatsapp':
                        return <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />;
                      case 'Globe':
                      case 'Website':
                      case 'Web':
                        return <Globe className="w-4 h-4 text-cyan-400" />;
                      case 'Video':
                      case 'Youtube':
                      case 'youtube':
                        return <YouTubeIcon className="w-4 h-4 text-[#FF0000]" />;
                      case 'Instagram':
                      case 'instagram':
                        return <InstagramIcon className="w-4 h-4 text-[#E4405F]" />;
                      case 'LinkedIn':
                      case 'linkedin':
                        return <LinkedInIcon className="w-4 h-4 text-[#0A66C2]" />;
                      case 'TikTok':
                      case 'tiktok':
                        return <TikTokIcon className="w-4 h-4 text-[#00f2fe]" />;
                      case 'XTwitter':
                      case 'Twitter':
                      case 'twitter':
                        return <XTwitterIcon className="w-4 h-4 text-white" />;
                      case 'GitHub':
                      case 'github':
                        return <GitHubIcon className="w-4 h-4 text-white" />;
                      case 'Book':
                        return <Book className="w-4 h-4 text-purple-400" />;
                      case 'Award':
                        return <Award className="w-4 h-4 text-amber-400" />;
                      default:
                        return <ExternalLink className="w-4 h-4 text-[#0FFF55]" />;
                    }
                  };

                  return (
                    <div
                      key={link.id}
                      className={`flex items-center justify-between bg-[#121316] border p-3.5 rounded-xl text-xs transition-colors ${
                        editingLinkId === link.id ? 'border-[#0FFF55] bg-[#0FFF55]/5' : 'border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3 truncate flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {renderItemIcon()}
                        </div>
                        <div className="truncate">
                          <div className="font-bold text-white truncate flex items-center gap-2">
                            <span>{link.title}</span>
                            {link.badgeText && (
                              <span className="text-[9px] bg-[#0FFF55]/20 text-[#0FFF55] px-2 py-0.5 rounded-full uppercase font-black">
                                {link.badgeText}
                              </span>
                            )}
                          </div>
                          <div className="text-gray-400 text-[11px] truncate">{link.url}</div>
                        </div>
                      </div>

                      {/* Botões de Ação: Subir, Descer, Editar, Excluir */}
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        type="button"
                        onClick={() => handleMoveBioLink(idx, 'up')}
                        disabled={idx === 0}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        title="Mover para cima"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleMoveBioLink(idx, 'down')}
                        disabled={idx === currentBio.links.length - 1}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        title="Mover para baixo"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleStartEditBioLink(link)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-[#0FFF55] hover:bg-white/10 transition-all"
                        title="Editar Link"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteBioLink(link.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/10 transition-all"
                        title="Excluir Link"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* TAB: GERAL & BRANDING */}
          {activeTab === 'geral' && (
            <form onSubmit={handleSaveGeneral} className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">
                  URL Slug Personalizada (ex: /mentoria-sua-url)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-mono">/mentoria-</span>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="flex-1 px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Título do Curso</label>
                  <input
                    type="text"
                    required
                    value={formData.courseTitle}
                    onChange={(e) => setFormData({ ...formData, courseTitle: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Tagline do Hero</label>
                  <input
                    type="text"
                    value={formData.tagline || ''}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Nome do Mentor</label>
                  <input
                    type="text"
                    required
                    value={formData.mentorName}
                    onChange={(e) => setFormData({ ...formData, mentorName: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-300 mb-1">Título/Especialidade do Mentor</label>
                  <input
                    type="text"
                    value={formData.mentorTitle}
                    onChange={(e) => setFormData({ ...formData, mentorTitle: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <ImageUploader
                    bucketName="mentorias"
                    label="Foto de Perfil / Avatar (Drag & Drop)"
                    value={formData.mentorAvatarUrl}
                    onChange={(url) => {
                      const updated = {
                        ...formData,
                        mentorAvatarUrl: url,
                        bioConfig: {
                          ...(formData.bioConfig || {}),
                          bioAvatarUrl: url,
                        }
                      };
                      setFormData(updated);
                    }}
                    options={{ maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' }}
                    placeholder="Arraste a foto ou selecione o arquivo..."
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    * Proporção ideal: <strong className="text-[#0FFF55]">400x400 (1:1)</strong>. Atualiza automaticamente a foto do Link da Bio.
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-300 mb-1">Cor de Destaque (Hex)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formData.branding.accentColor || '#0FFF55'}
                        onChange={(e) => setFormData({ ...formData, branding: { ...formData.branding, accentColor: e.target.value } })}
                        className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0"
                      />
                      <input
                        type="text"
                        value={formData.branding.accentColor || '#0FFF55'}
                        onChange={(e) => setFormData({ ...formData, branding: { ...formData.branding, accentColor: e.target.value } })}
                        className="flex-1 px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ImageUploader
                  bucketName="mentorias"
                  label="Imagem de Capa (Banner Hero - Drag & Drop)"
                  value={formData.branding.coverImageUrl}
                  onChange={(url) => {
                    const updated = {
                      ...formData,
                      branding: { ...formData.branding, coverImageUrl: url }
                    };
                    setFormData(updated);
                  }}
                  options={{ maxWidth: 1200, maxHeight: 500, quality: 0.85, format: 'webp' }}
                  placeholder="Arraste o banner ou selecione o arquivo..."
                />
                <span className="text-[10px] text-gray-400 mt-1 block">
                  * Proporção ideal: <strong className="text-[#0FFF55]">1200x500</strong>.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Duração Limite do Acesso à Mentoria / Curso (em dias)</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={formData.accessDurationDays ?? 365}
                    onChange={(e) => setFormData({ ...formData, accessDurationDays: parseInt(e.target.value) || 365 })}
                    className="w-36 px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                  />
                  <span className="text-xs text-gray-400">
                    Padrão recomendado: <strong className="text-[#0FFF55]">365 dias (1 ano)</strong>. Altere conforme necessário para novos alunos.
                  </span>
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs shadow-lg transition-all active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Salvar Alterações de Branding</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB: VÍDEO HERO */}
          {activeTab === 'hero' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Título do Vídeo Hero</label>
                <input
                  type="text"
                  value={formData.heroSpoiler.title}
                  onChange={(e) => setFormData({ ...formData, heroSpoiler: { ...formData.heroSpoiler, title: e.target.value } })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Link do Vídeo do YouTube (Trailer / Introdução)</label>
                <input
                  type="url"
                  value={formData.heroSpoiler.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, heroSpoiler: { ...formData.heroSpoiler, youtubeUrl: e.target.value } })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Descrição Breve</label>
                <textarea
                  rows={3}
                  value={formData.heroSpoiler.description}
                  onChange={(e) => setFormData({ ...formData, heroSpoiler: { ...formData.heroSpoiler, description: e.target.value } })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                />
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    onSaveConfig(formData);
                    onClose();
                  }}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs shadow-lg transition-all active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Salvar Vídeo Hero</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB: MÓDULOS & AULAS */}
          {activeTab === 'modulos' && (
            <div className="space-y-6">
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase text-[#0FFF55]">Criar Novo Módulo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Título do Módulo (ex: ENGENHARIA DE PROMPT)"
                    value={newModuleTitle}
                    onChange={(e) => setNewModuleTitle(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Descrição breve do módulo"
                    value={newModuleDesc}
                    onChange={(e) => setNewModuleDesc(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                  <input
                    type="url"
                    placeholder="URL Imagem de Capa Pôster"
                    value={newModuleCover}
                    onChange={(e) => setNewModuleCover(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddModule}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs rounded-xl"
                >
                  <Plus className="w-4 h-4" />
                  <span>Adicionar Módulo</span>
                </button>
              </div>

              <div className="space-y-4">
                {formData.modules.map((mod) => (
                  <div key={mod.id} className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                      <div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Módulo {mod.order}</span>
                        <h4 className="text-sm font-bold text-white">{mod.title}</h4>
                      </div>
                      <button
                        onClick={() => handleDeleteModule(mod.id)}
                        className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-white/5"
                        title="Excluir Módulo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-2 pl-2">
                      {mod.lessons.map((les) => (
                        <div key={les.id} className="flex items-center justify-between bg-[#17181c] px-3 py-2 rounded-xl text-xs">
                          <div className="flex items-center gap-2 truncate">
                            <Video className="w-3.5 h-3.5 text-[#0FFF55]" />
                            <span className="font-semibold text-white truncate">{les.title}</span>
                            <span className="text-gray-500 text-[10px]">({les.durationText})</span>
                          </div>
                          <button
                            onClick={() => handleDeleteLesson(mod.id, les.id)}
                            className="text-gray-500 hover:text-red-400 p-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {selectedModuleIdForLesson === mod.id ? (
                      <div className="bg-[#17181c] border border-white/10 p-3 rounded-xl space-y-2 mt-2">
                        <input
                          type="text"
                          placeholder="Título da Aula"
                          value={newLessonTitle}
                          onChange={(e) => setNewLessonTitle(e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#0d0e11] border border-white/15 rounded-lg text-xs text-white"
                        />
                        <input
                          type="url"
                          placeholder="Link da Aula no YouTube"
                          value={newLessonYoutube}
                          onChange={(e) => setNewLessonYoutube(e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#0d0e11] border border-white/15 rounded-lg text-xs text-white"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Duração (ex: 15 min)"
                            value={newLessonDuration}
                            onChange={(e) => setNewLessonDuration(e.target.value)}
                            className="w-1/3 px-3 py-1.5 bg-[#0d0e11] border border-white/15 rounded-lg text-xs text-white"
                          />
                          <input
                            type="text"
                            placeholder="Descrição breve"
                            value={newLessonDesc}
                            onChange={(e) => setNewLessonDesc(e.target.value)}
                            className="w-2/3 px-3 py-1.5 bg-[#0d0e11] border border-white/15 rounded-lg text-xs text-white"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <button
                            onClick={() => setSelectedModuleIdForLesson('')}
                            className="px-3 py-1 text-xs text-gray-400"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleAddLesson(mod.id)}
                            className="px-3 py-1 bg-[#0FFF55] text-black font-bold text-xs rounded-lg"
                          >
                            Salvar Aula
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedModuleIdForLesson(mod.id)}
                        className="flex items-center gap-1 text-xs text-[#0FFF55] font-bold hover:underline pt-1"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Adicionar Aula neste Módulo</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB: EVENTOS */}
          {activeTab === 'eventos' && (
            <div className="space-y-6 max-w-2xl">
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase text-[#0FFF55]">Cadastrar Novo Evento / Call Ao Vivo</h3>
                <input
                  type="text"
                  placeholder="Título do Evento (ex: Call Tira-Dúvidas Semanal)"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  className="w-full px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Data (ex: Quinta-feira / 20 de Ago)"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                  <input
                    type="text"
                    placeholder="Horário (ex: 20:00)"
                    value={newEventTime}
                    onChange={(e) => setNewEventTime(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                </div>
                <input
                  type="url"
                  placeholder="Link da Reunião (Zoom / Google Meet / YouTube Live)"
                  value={newEventUrl}
                  onChange={(e) => setNewEventUrl(e.target.value)}
                  className="w-full px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                />
                <textarea
                  rows={2}
                  placeholder="Descrição do evento"
                  value={newEventDesc}
                  onChange={(e) => setNewEventDesc(e.target.value)}
                  className="w-full px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                />
                <button
                  type="button"
                  onClick={handleAddEvent}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs rounded-xl"
                >
                  <Plus className="w-4 h-4" />
                  <span>Cadastrar Evento</span>
                </button>
              </div>

              <div className="space-y-3">
                {(formData.events || []).map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between bg-[#121316] border border-white/10 p-4 rounded-xl text-xs">
                    <div>
                      <div className="font-bold text-white text-sm">{ev.title}</div>
                      <div className="text-gray-400">{ev.dateText} às {ev.timeText}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(ev.id)}
                      className="text-gray-500 hover:text-red-400 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB: SUPORTE WHATSAPP */}
          {activeTab === 'suporte' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Número do WhatsApp com DDD (ex: 5511999999999)</label>
                <input
                  type="text"
                  value={formData.support?.whatsappNumber || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    support: { ...formData.support, whatsappNumber: e.target.value }
                  })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">URL da Foto de Perfil do Suporte</label>
                <input
                  type="url"
                  value={formData.support?.photoUrl || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    support: { ...formData.support, photoUrl: e.target.value }
                  })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1">Mensagem Padrão Inicial</label>
                <input
                  type="text"
                  value={formData.support?.message || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    support: { ...formData.support, message: e.target.value }
                  })}
                  className="w-full px-3.5 py-2.5 bg-[#0d0e11] border border-white/15 rounded-xl text-xs text-white"
                />
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    onSaveConfig(formData);
                    onClose();
                  }}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs shadow-lg transition-all active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Salvar Suporte</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB: DASHBOARD DE ALUNOS */}
          {activeTab === 'alunos' && (
            <div className="space-y-6">
              <div className="bg-[#121316] border border-white/10 rounded-2xl p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase text-[#0FFF55]">Dar Acesso a Novo Aluno</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Nome do Aluno"
                    value={newStudentName}
                    onChange={(e) => setNewStudentName(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                  <input
                    type="email"
                    placeholder="Email do Aluno"
                    value={newStudentEmail}
                    onChange={(e) => setNewStudentEmail(e.target.value)}
                    className="px-3.5 py-2 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddStudent}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs rounded-xl"
                >
                  <Plus className="w-4 h-4" />
                  <span>Liberar Acesso</span>
                </button>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase">Alunos Matriculados ({ (formData.students || []).length })</h4>
                {(formData.students || [
                  { id: 'st-1', name: 'João Silva', email: 'joao@email.com', accessDateText: '01/08/2026', progressPercentage: 45 },
                  { id: 'st-2', name: 'Maria Souza', email: 'maria@email.com', accessDateText: '03/08/2026', progressPercentage: 80 }
                ]).map((st) => (
                  <div key={st.id} className="flex items-center justify-between bg-[#121316] border border-white/10 p-3 rounded-xl text-xs">
                    <div>
                      <div className="font-bold text-white">{st.name}</div>
                      <div className="text-gray-400 text-[11px]">{st.email} • Acesso em {st.accessDateText}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#0FFF55] font-bold text-xs">{st.progressPercentage}% concluído</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal QR Code */}
      <BioQRCodeModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        title={currentBio.bioTitle}
        url={bioPageUrl}
        avatarUrl={currentBio.bioAvatarUrl || formData.mentorAvatarUrl}
        accentColor={accentColor}
      />
    </div>
  );
};
